import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

interface BusinessCustomer {
  id: string;
  user_id: string;
  company_name: string;
  registration_number: string | null;
  vat_number: string | null;
  contact_name: string;
  email: string;
  phone: string | null;
  billing_address: string | null;
  city: string | null;
  postal_code: string | null;
  country: string;
  delivery_address: string | null;
  delivery_city: string | null;
  delivery_postal_code: string | null;
  delivery_country: string | null;
  discount_percentage: number;
  payment_terms_days: number;
  is_approved: boolean;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  businessCustomer: BusinessCustomer | null;
  isLoading: boolean;
  isBusinessCustomer: boolean;
  isApprovedBusinessCustomer: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string, businessData: Partial<BusinessCustomer>) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  refreshBusinessCustomer: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [businessCustomer, setBusinessCustomer] = useState<BusinessCustomer | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchBusinessCustomer = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('business_customers')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();

      if (error) {
        console.error('Error fetching business customer:', error);
        return null;
      }
      return data as BusinessCustomer | null;
    } catch (error) {
      console.error('Error fetching business customer:', error);
      return null;
    }
  };

  const refreshBusinessCustomer = async () => {
    if (user) {
      const customer = await fetchBusinessCustomer(user.id);
      setBusinessCustomer(customer);
    }
  };

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);

        // Defer Supabase calls with setTimeout
        if (session?.user) {
          setTimeout(() => {
            fetchBusinessCustomer(session.user.id).then(setBusinessCustomer);
          }, 0);
        } else {
          setBusinessCustomer(null);
        }
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      
      if (session?.user) {
        fetchBusinessCustomer(session.user.id).then((customer) => {
          setBusinessCustomer(customer);
          setIsLoading(false);
        });
      } else {
        setIsLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      return { error };
    } catch (error) {
      return { error: error as Error };
    }
  };

  const signUp = async (email: string, password: string, businessData: Partial<BusinessCustomer>) => {
    try {
      const redirectUrl = `${window.location.origin}/`;
      
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: redirectUrl,
        },
      });

      if (error) {
        return { error };
      }

      // Create business customer profile
      if (data.user) {
        const { error: profileError } = await supabase
          .from('business_customers')
          .insert({
            user_id: data.user.id,
            company_name: businessData.company_name || '',
            registration_number: businessData.registration_number,
            vat_number: businessData.vat_number,
            contact_name: businessData.contact_name || '',
            email: email,
            phone: businessData.phone,
            billing_address: businessData.billing_address,
            city: businessData.city,
            postal_code: businessData.postal_code,
            country: businessData.country || 'Latvia',
            is_approved: false,
          });

        if (profileError) {
          console.error('Error creating business profile:', profileError);
          return { error: profileError };
        }
      }

      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setBusinessCustomer(null);
  };

  const value: AuthContextType = {
    user,
    session,
    businessCustomer,
    isLoading,
    isBusinessCustomer: !!businessCustomer,
    isApprovedBusinessCustomer: !!businessCustomer?.is_approved,
    signIn,
    signUp,
    signOut,
    refreshBusinessCustomer,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export type { BusinessCustomer };
